// @flow
import type { Saga } from "redux-saga";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import queryString from "query-string";
import { makeApiCall } from "redux/middleware/api";
import type { BusinessLocationRecord } from "redux/modules/createCabelasClass/records/BusinessLocationRecord";
import type { BizhubEvent } from "redux/modules/instructorCabelasClasses/parsers";
import {
  BIZHUB_API_ENDPOINTS,
  EVENTS_API_ENDPOINTS,
} from "redux/modules/createCabelasClass/constants";
import {
  ALL_SUBMITTED_STATUSES,
  CANCELLED_STATUS,
  DESC,
  SUBMITTED_VIEW,
  cabelasClassesRequestError,
  receiveCabelasClasses,
  requestCabelasClasses,
} from "redux/modules/instructorCabelasClasses/instructorCabelasClasses";
import type { NormalizedCabelasClassesParams } from "redux/modules/instructorCabelasClasses/instructorCabelasClasses";
import { CHAPTER_EVENT_TYPES } from "redux/modules/instructorChapters/constants";
import { hasChapterLocations } from "redux/modules/instructorChapters/instructorChapters";
import type { State as ChaptersState } from "redux/modules/instructorChapters/instructorChapters";
import { isChapterLocation } from "redux/modules/businessEventScheduling/instructorLocations";
import { REQUEST } from "./instructorChapterEvents";
import type { ChapterEventsAction } from "./instructorChapterEvents";

type ContextState = {
  auth: { userId: ?string },
  instructorChapters: ChaptersState,
};
const getBusinessIds = ({
  auth,
  instructorChapters,
}: ContextState): Array<string> =>
  hasChapterLocations(instructorChapters, auth.userId)
    ? Array.from(
        new Set(
          instructorChapters.locations
            .filter(isChapterLocation)
            .map(
              (location: BusinessLocationRecord): string => location.businessId
            )
        )
      )
    : [];

type EventsResponse = {
  data: Array<BizhubEvent>,
  meta: { totalPages: number },
  errors?: mixed,
  error?: mixed,
};
const checkEvents = (json: ?EventsResponse) => {
  if (
    !json ||
    json.errors ||
    json.error ||
    !Array.isArray(json.data) ||
    !Number.isInteger(json.meta?.totalPages) ||
    json.meta.totalPages < 0 ||
    json.data.some(
      (event: BizhubEvent): boolean =>
        !event ||
        typeof event.id !== "string" ||
        !event.id ||
        !event.attributes ||
        typeof event.attributes !== "object"
    )
  ) {
    throw new Error("Unable to load all Chapter events. Try again.");
  }
};

const eventFilters = ({
  eventStatus,
  reviewStatus,
  eventView,
  search,
}: NormalizedCabelasClassesParams): { [string]: string } => {
  // The API's _in operator accepts a JSON array, not comma-separated values.
  const filters: { [string]: string } = {
    "filter[type][_in]": JSON.stringify(CHAPTER_EVENT_TYPES),
  };

  if (eventStatus === ALL_SUBMITTED_STATUSES) {
    filters["filter[status][_neq]"] = CANCELLED_STATUS;
  } else if (eventStatus) {
    filters["filter[status][_eq]"] = eventStatus;
  }

  if (eventView === SUBMITTED_VIEW && reviewStatus) {
    filters["filter[reviewStatus][_eq]"] = reviewStatus;
  }

  if (search.trim()) {
    filters["filter[name][_contains]"] = search.trim();
  }

  return filters;
};

const compareEvents =
  (
    sortColumn: string,
    sortDirection: string
  ): ((a: BizhubEvent, b: BizhubEvent) => number) =>
  (a: BizhubEvent, b: BizhubEvent): number => {
    let comparison;
    if (sortColumn === "price") {
      comparison = (a.attributes.price || 0) - (b.attributes.price || 0);
    } else if (sortColumn === "name") {
      comparison = (a.attributes.name || "").localeCompare(
        b.attributes.name || ""
      );
    } else {
      comparison = (a.attributes.startDate || "").localeCompare(
        b.attributes.startDate || ""
      );
    }
    const result = comparison || a.id.localeCompare(b.id);
    return sortDirection === DESC ? -result : result;
  };

export function* loadChapterBusinessEvents(
  businessId: string,
  filters: { [string]: string }
): Saga<Array<BizhubEvent>> {
  const events = [];
  let page = 1;
  let totalPages = 1;
  do {
    const json = yield call(
      makeApiCall,
      "GET",
      `${BIZHUB_API_ENDPOINTS.BUSINESS_EVENTS(
        businessId
      )}?${queryString.stringify({
        ...filters,
        page,
        perPage: 50,
        sort: "id",
      })}`,
      undefined,
      false
    );
    checkEvents(json);
    events.push(...json.data);
    totalPages = json.meta.totalPages;
    page += 1;
  } while (page <= totalPages);
  return events;
}

export function* performChapterEvents(action: ChapterEventsAction): Saga<void> {
  const request = requestCabelasClasses(action.payload);
  const params = request.payload;
  yield put(request);
  const filters = eventFilters(params);
  try {
    // Submitted is combined across Chapter businesses by the API; Upcoming and
    // Completed come from each business's hosted events and are merged here.
    if (params.eventView === SUBMITTED_VIEW) {
      const sortPrefix = params.sortDirection === DESC ? "-" : "";
      const json = yield call(
        makeApiCall,
        "GET",
        `${EVENTS_API_ENDPOINTS.ALL_EVENTS}?${queryString.stringify({
          ...filters,
          page: params.currentPage,
          perPage: params.perPage,
          sort: `${sortPrefix}${params.sortColumn}`,
        })}`,
        undefined,
        false
      );
      checkEvents(json);
      yield put(receiveCabelasClasses(json, params));
      return;
    }

    const businessIds = yield select(getBusinessIds);
    if (!businessIds.length) {
      throw new Error("No assigned chapters are available.");
    }
    const groups = yield all(
      businessIds.map((businessId: string): mixed =>
        call(loadChapterBusinessEvents, businessId, filters)
      )
    );
    const byId = new Map<string, BizhubEvent>();
    groups.forEach((events: Array<BizhubEvent>) => {
      events.forEach((event: BizhubEvent) => {
        byId.set(event.id, event);
      });
    });
    const events = Array.from(byId.values()).sort(
      compareEvents(params.sortColumn, params.sortDirection)
    );
    const totalPages = Math.max(1, Math.ceil(events.length / params.perPage));
    const currentPage = Math.min(params.currentPage, totalPages);
    const data = events.slice(
      (currentPage - 1) * params.perPage,
      currentPage * params.perPage
    );
    yield put(
      receiveCabelasClasses(
        {
          data,
          meta: {
            count: data.length,
            total: events.length,
            perPage: params.perPage,
            currentPage,
            totalPages,
          },
        },
        params
      )
    );
  } catch (error) {
    yield put(
      cabelasClassesRequestError({
        errorMessage:
          error?.message || "Unable to load Chapter events. Try again.",
        actionParams: { requestId: params.requestId },
      })
    );
  }
}

export function* watchChapterEvents(): Saga<void> {
  yield takeLatest(REQUEST, performChapterEvents);
}
